/**
 * useWizardState — localStorage-backed wizard state composable.
 *
 * Tracks: current step, step history with timestamps, revision counts,
 * draft storage per step, and staleness (7 days).
 */
import { ref } from 'vue'

const STORAGE_PREFIX = 'mystic_wizard_'
const STALE_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

function storageKey(userId) {
  return `${STORAGE_PREFIX}${userId || 'anon'}`
}

// A fresh wizard opens at the threshold. 'theme' (the setting picker) was the
// old default and rendered for the beat before mount resolved the real step.
function freshState(initialStep = 'conceiving') {
  return {
    currentStep: initialStep,
    history: [{ step: initialStep, ts: Date.now() }],
    revisions: {},
    drafts: {},
    createdAt: Date.now(),
    updatedAt: Date.now(),
  }
}

export function useWizardState(userId) {
  const key = storageKey(userId)

  // Load or create
  let loaded = null
  try {
    const raw = localStorage.getItem(key)
    if (raw) loaded = JSON.parse(raw)
  } catch { /* corrupt — start fresh */ }

  // Staleness check
  if (loaded && (Date.now() - loaded.createdAt) > STALE_MS) {
    loaded = null
  }

  const state = ref(loaded || freshState())

  function save() {
    state.value.updatedAt = Date.now()
    try {
      localStorage.setItem(key, JSON.stringify(state.value))
    } catch { /* quota exceeded — silent */ }
  }

  function goTo(step, data) {
    const prev = state.value.currentStep
    state.value.currentStep = step
    state.value.history.push({ step, ts: Date.now(), from: prev })
    // Track revisions
    state.value.revisions[step] = (state.value.revisions[step] || 0) + 1
    // Merge data into drafts if provided
    if (data) {
      state.value.drafts[step] = { ...(state.value.drafts[step] || {}), ...data }
    }
    save()
  }

  function goBack() {
    if (state.value.history.length <= 1) return
    state.value.history.pop()
    const prev = state.value.history[state.value.history.length - 1]
    state.value.currentStep = prev.step
    save()
  }

  function canGoBack() {
    return state.value.history.length > 1
  }

  function setDraft(step, data) {
    state.value.drafts[step] = data
    save()
  }

  function getDraft(step) {
    return state.value.drafts[step] || null
  }

  function reset() {
    state.value = freshState()
    save()
  }

  // Save initial state
  save()

  return {
    state,
    goTo,
    goBack,
    canGoBack,
    setDraft,
    getDraft,
    reset,
    save,
  }
}
